import LegalLayout from "@/components/LegalLayout";

function H2({ children }) {
  return (
    <h2 className="mt-10 font-heading text-2xl md:text-3xl"
      style={{ fontVariationSettings: "'opsz' 64, 'wght' 500", color: "var(--ink)" }}>{children}</h2>
  );
}

function P({ children }) { return <p>{children}</p>; }

export default function About() {
  return (
    <LegalLayout
      eyebrow="v. The story"
      title="About"
      italic="Pintaflow."
      lastUpdated="July 17, 2026"
      testid="about-page"
    >
      <P>
        PintaFlow is a Creator OS — an unhurried, thoughtful workspace for people who take their content seriously without taking themselves too seriously. It is built for creators who want <em>consistency over virality, craft over hustle, and momentum over metrics</em>.
      </P>

      <H2>The problem</H2>
      <P>
        Most creators live inside a chaos of Notion pages, Google Docs, phone notes, sticky reminders, and half-written scripts scattered across three devices. They know what they want to make — the friction is everywhere <em>between</em> the idea and the publish button. PintaFlow was built to close that gap.
      </P>

      <H2>The philosophy</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong style={{ color: "var(--ink)" }}>3 moves today, everything else can wait.</strong> The daily view surfaces only what actually matters right now.</li>
        <li><strong style={{ color: "var(--ink)" }}>Momentum over metrics.</strong> Views are outcomes; consistency, planning and engagement are the causes. We measure the causes.</li>
        <li><strong style={{ color: "var(--ink)" }}>No judgment, ever.</strong> Weak areas are called Growth Opportunities. Streaks are gentle, not punishing.</li>
        <li><strong style={{ color: "var(--ink)" }}>Espresso &amp; Brass.</strong> The design borrows from private members&apos; clubs and old cafés — dark, warm, unhurried. Not another sterile SaaS dashboard.</li>
      </ul>

      <H2>The founder</H2>
      <P>
        Pintaflow is a solo project by <strong style={{ color: "var(--ink)" }}>Pradeep Munjal</strong>, a creator and builder based in <strong style={{ color: "var(--ink)" }}>Delhi, India</strong>. Pintaflow was built in public — every feature ships from a real creator&apos;s daily workflow, tested by real creators before it hits the mainline.
      </P>
      <P>
        You can follow the build on Twitter <a href="https://twitter.com/pradeepmunjal" style={{ color: "var(--seal)" }} target="_blank" rel="noreferrer">@pradeepmunjal</a> — many features arrive from replies there.
      </P>

      <H2>What we&apos;re building next</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Deeper YouTube + Instagram Analytics with AI-detected patterns.</li>
        <li>Idea Vault Virality Score — predicted reach for every idea before you invest time.</li>
        <li>Weekly Growth Report — a Sunday email with your wins, gaps and next week&apos;s focus.</li>
        <li>Collaborative workspaces for creator teams.</li>
      </ul>

      <H2>The promise</H2>
      <P>
        We&apos;ll never sell your data. We&apos;ll never spam you. We&apos;ll never ship a feature we don&apos;t use ourselves. And we&apos;ll never make you feel bad about a day you missed — because that&apos;s not how consistency works.
      </P>

      <H2>Say hi</H2>
      <P>
        Questions, feedback, collab ideas, or a hello — email <a href="mailto:hello@pintaflow.com" style={{ color: "var(--seal)" }}>hello@pintaflow.com</a>. Pradeep reads every message.
      </P>
    </LegalLayout>
  );
}
